import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {YesNoModal} from '../../elements/YesNoModal';
import {ClientEconomicsApi} from '../../../api/clientEconomicsApi';
import {formatFullMoney} from '../panel_v11/panelFormatters';
import {economyReasonText, formatPct, lineCurrencies, lineDerivedReason} from '../panel_v11/economyFormatters';
import {EconomiaForm} from './EconomiaForm';
import {
    currencyOptions,
    invalidFieldsOf,
    toPayload,
    validateEconomics,
    valuesFromProfile,
} from './state/economiaModel';

/**
 * «Por línea de seguimiento» (mockup 2, abajo): una línea que vende otra cosa —mayorista, otra
 * marca— puede tener sus propios números. Sin override HEREDA del cliente y lo dice con el valor;
 * «Personalizar» abre el MISMO formulario precargado, y «Volver a heredar» desactiva el override.
 *
 * Una línea en otra moneda que el perfil no puede heredar (el backend no convierte): se avisa en la
 * fila, con el texto de la card, porque la salida es justamente personalizarla. El motivo lo dice el
 * backend (`unavailable_reason.derived`): también ve la CUENTA de la línea en otra moneda, que desde
 * acá no se ve (R-ecofe-28).
 *
 * «Volver a heredar» se confirma: desactiva el override entero y no tiene deshacer (R-ecofe-16).
 */
export function EconomiaLines({data, clientId, onChanged}) {
    const {t} = useTranslation();
    const [helpOpen, setHelpOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [busyLine, setBusyLine] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(null);
    // Dos «Guardar» en el mismo tick salían como dos versiones: el `saving` del estado llega tarde.
    const saving = useRef(false);

    const lines = (data && Array.isArray(data.lines)) ? data.lines : [];
    if (!lines.length) return null;
    const profile = (data && data.profile) || {};
    const profileCurrency = data.currency || profile.currency || null;

    const startEdit = (line) => {
        // Precargado con lo que la línea usa HOY: su override si tiene, si no lo que hereda. La moneda
        // arranca en la de la línea (el caso que obliga a personalizar es justamente el de otra moneda).
        const base = line.override ? {...data, profile: line.override, currency: line.override.currency} : data;
        const values = valuesFromProfile(base);
        if (!line.override && line.currency) values.currency = line.currency;
        setEditing({lineId: line.business_unit_id, values, errors: {}, showError: false, saving: false});
    };

    const changeEditing = (field, value) => setEditing((current) => ({
        ...current,
        values: {...current.values, [field]: value, ...(field === 'avg_ticket' ? {avgTicketTouched: true} : {})},
    }));

    const saveOverride = () => {
        if (saving.current) return;
        const check = validateEconomics(editing.values);
        if (!check.valid) {
            setEditing((current) => ({...current, errors: check.fields, showError: true}));
            return;
        }
        saving.current = true;
        setEditing((current) => ({...current, saving: true, showError: false, errors: {}}));
        const fail = (response) => {
            saving.current = false;
            const fields = invalidFieldsOf(response);
            if (!fields) toast.error(t('economics.error.generic'));
            setEditing((current) => current && ({...current, saving: false, showError: !!fields,
                errors: fields || {}}));
        };
        ClientEconomicsApi.saveLineOverride(clientId, editing.lineId, toPayload(editing.values)).then((response) => {
            if (!response || response.status !== 'OK') {
                fail(response);
                return;
            }
            saving.current = false;
            const saved = response.data && response.data.saved;
            toast.success(t('economics.form.saved', {version: (saved && saved.version) || '—'}));
            setEditing(null);
            if (onChanged) onChanged(response.data || null);
        }).catch(() => fail(null));
    };

    const removeOverride = () => {
        const line = confirmRemove;
        setConfirmRemove(null);
        if (!line) return;
        setBusyLine(line.business_unit_id);
        ClientEconomicsApi.removeLineOverride(clientId, line.business_unit_id).then((response) => {
            setBusyLine(null);
            if (!response || response.status !== 'OK') {
                toast.error(t('economics.error.generic'));
                return;
            }
            if (onChanged) onChanged(response.data || null);
        }).catch(() => {
            setBusyLine(null);
            toast.error(t('economics.error.generic'));
        });
    };

    const renderValues = (line) => {
        if (!line.override) {
            return (
                <span className="eco-line__values -inherited">
                    {t('economics.lines.inherits', {
                        value: formatPct(profile.gross_margin_pct) || '—',
                        interpolation: {escapeValue: false},
                    })}
                </span>
            );
        }
        const override = line.override;
        return (
            <span className="eco-line__values">
                <span className="eco-chip">{t('client_panel.economy.line.override')}</span>
                <span>{`${t('client_panel.economy.input.gross_margin_pct')}: `}
                    <strong>{formatPct(override.gross_margin_pct) || '—'}</strong></span>
                <span>{`${t('client_panel.economy.input.shipping_per_order')}: `}
                    <strong>{formatFullMoney(override.currency || line.currency, override.shipping_per_order) || '—'}</strong></span>
            </span>
        );
    };

    return (
        <section className="eco-fs eco-lines-section" aria-labelledby="eco-lines-title" data-testid="eco-lines">
            <div className="eco-fs__head">
                <h2 className="eco-fs__title" id="eco-lines-title">{t('economics.lines.title')}</h2>
                <button type="button" className="eco-link" aria-expanded={helpOpen}
                        onClick={() => setHelpOpen((open) => !open)}>
                    {t('economics.lines.help')}
                </button>
            </div>
            {helpOpen && <p className="eco-fs__help">{t('economics.lines.help_body')}</p>}

            <ul className="eco-line-list">
                {lines.map((line) => {
                    const isEditing = editing && editing.lineId === line.business_unit_id;
                    const mismatch = lineDerivedReason(line) === 'CURRENCY_MISMATCH';
                    return (
                        <li className={`eco-line${line.override ? ' -override' : ''}`} key={line.business_unit_id}
                            data-testid={`eco-line-${line.business_unit_id}`}>
                            <div className="eco-line__row">
                                <span className="eco-line__name">
                                    {line.name}
                                    {line.currency && <span className="eco-line__currency">{line.currency}</span>}
                                    {line.has_store === false && (
                                        <span className="eco-chip -muted">{t('client_panel.economy.line.no_store')}</span>
                                    )}
                                </span>
                                {renderValues(line)}
                                <span className="eco-line__acts">
                                    {!isEditing && (
                                        <button type="button" className="eco-link" onClick={() => startEdit(line)}>
                                            {line.override ? t('client_panel.economy.edit') : t('economics.lines.customize')}
                                        </button>
                                    )}
                                    {line.override && !isEditing && (
                                        <button type="button" className="eco-link -muted"
                                                disabled={busyLine === line.business_unit_id}
                                                onClick={() => setConfirmRemove(line)}>
                                            {t('economics.lines.remove_override')}
                                        </button>
                                    )}
                                </span>
                            </div>
                            {mismatch && (
                                <p className="eco-line__warn">
                                    {economyReasonText('CURRENCY_MISMATCH', t, lineCurrencies(line, profileCurrency))}
                                </p>
                            )}
                            {isEditing && (
                                <div className="eco-line__form">
                                    <EconomiaForm
                                        idPrefix={`eco-line-${line.business_unit_id}`}
                                        values={editing.values}
                                        errors={editing.errors}
                                        currencies={currencyOptions(data, editing.values.currency)}
                                        onChange={changeEditing}
                                        onSubmit={saveOverride}
                                        onCancel={() => setEditing(null)}
                                        saving={editing.saving}
                                        showError={editing.showError}
                                        showNote={false}
                                    />
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>

            <YesNoModal
                isOpen={!!confirmRemove}
                skipRenderButton={true}
                title={t('economics.lines.remove_override')}
                message={t('economics.lines.remove_override_confirm')}
                question=""
                onYesAnswer={removeOverride}
                onNoAnswer={() => setConfirmRemove(null)}
            />
        </section>
    );
}

export default EconomiaLines;
